import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Play, Download, Trash2, BarChart3, TrendingUp, Target, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { ProgressiveBacktestService, type ProgressiveBacktestConfig, type ProgressiveBacktestResult, type StoredBacktestSimulation } from '@/services/ProgressiveBacktestService';
import { type TradingFile } from '@/services/TradingPatternAnalysisService';
import { AnalysisMode } from '@/types/trading-analysis-types';
import { SimulationDetailDialog } from './SimulationDetailDialog';

interface ProgressiveBacktestTabProps {
  selectedFile: TradingFile | null;
  selectedAnalysisMode: AnalysisMode;
}

export const ProgressiveBacktestTab: React.FC<ProgressiveBacktestTabProps> = ({
  selectedFile,
  selectedAnalysisMode
}) => {
  // Configuration state
  const [config, setConfig] = useState<ProgressiveBacktestConfig>({
    fileId: '',
    startPosition: 10,
    maxSequenceLength: 8,
    winThreshold: 0.52, // 52%
    analysisMode: selectedAnalysisMode
  });

  // Execution state
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<{ completed: number; total: number; accuracy: number } | null>(null);
  const [currentResult, setCurrentResult] = useState<ProgressiveBacktestResult | null>(null);
  
  // History state
  const [simulations, setSimulations] = useState<StoredBacktestSimulation[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState<StoredBacktestSimulation | null>(null);

  // Update file ID when selected file changes
  useEffect(() => {
    if (selectedFile?.id) {
      setConfig(prev => ({ ...prev, fileId: selectedFile.id }));
      loadSimulationHistory();
    }
  }, [selectedFile?.id]);

  // Update analysis mode when it changes
  useEffect(() => {
    setConfig(prev => ({ ...prev, analysisMode: selectedAnalysisMode }));
  }, [selectedAnalysisMode]);

  const loadSimulationHistory = async () => {
    if (!selectedFile?.id) return;
    
    setIsLoadingHistory(true);
    try {
      const result = await ProgressiveBacktestService.getSimulations(selectedFile.id);
      if (result.success && result.data) {
        setSimulations(result.data);
      } else {
        toast.error(result.error || 'Failed to load simulation history');
      }
    } catch (error) {
      console.error('Error loading simulation history:', error);
      toast.error('Failed to load simulation history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleRunBacktest = async () => {
    if (!selectedFile?.id) {
      toast.error('Please select a file first');
      return;
    }

    // Validate configuration
    const validation = ProgressiveBacktestService.validateConfig(config);
    if (!validation.isValid) {
      toast.error(`Configuration invalid: ${validation.errors.join(', ')}`);
      return;
    }

    setIsRunning(true);
    setProgress(null);
    setCurrentResult(null);

    try {
      const result = await ProgressiveBacktestService.runBacktest(
        config,
        (progressUpdate) => {
          setProgress(progressUpdate);
        }
      );

      if (result.success && result.result) {
        setCurrentResult(result.result);
        toast.success('Progressive Backtest completed successfully!');
        await loadSimulationHistory(); // Refresh history
      } else {
        toast.error(result.error || 'Backtest failed');
      }
    } catch (error) {
      console.error('Error running backtest:', error);
      toast.error('Failed to run backtest');
    } finally {
      setIsRunning(false);
      setProgress(null);
    }
  };

  const handleDownloadResults = () => {
    if (!currentResult) return;

    const csvContent = ProgressiveBacktestService.exportToCSV(currentResult);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `progressive_backtest_${selectedFile?.filename}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Results exported to CSV');
  };

  const handleDeleteSimulation = async (simulationId: string) => {
    if (!confirm('Are you sure you want to delete this simulation?')) return;

    try {
      const result = await ProgressiveBacktestService.deleteSimulation(simulationId);
      if (result.success) {
        toast.success('Simulation deleted successfully');
        await loadSimulationHistory();
        if (selectedSimulation?.id === simulationId) {
          setSelectedSimulation(null);
        }
      } else {
        toast.error(result.error || 'Failed to delete simulation');
      }
    } catch (error) {
      console.error('Error deleting simulation:', error);
      toast.error('Failed to delete simulation');
    }
  };

  const renderConfigurationForm = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Configuration
        </CardTitle>
        <CardDescription>
          Configure your progressive backtest parameters
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="start-position">Start Position</Label>
            <Input
              id="start-position"
              type="number"
              min="0"
              max={selectedFile?.total_trades || 1000}
              value={config.startPosition}
              onChange={(e) => setConfig(prev => ({ ...prev, startPosition: parseInt(e.target.value) || 0 }))}
            />
            <p className="text-xs text-muted-foreground">
              Position to start the backtest from (default: 10)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="analysis-mode">Analysis Mode</Label>
            <Select 
              value={config.analysisMode} 
              onValueChange={(value: AnalysisMode) => setConfig(prev => ({ ...prev, analysisMode: value }))}
            >
              <SelectTrigger id="analysis-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Max Sequence Length: {config.maxSequenceLength}</Label>
          <Slider
            value={[config.maxSequenceLength]}
            onValueChange={([value]) => setConfig(prev => ({ ...prev, maxSequenceLength: value }))}
            min={3}
            max={15}
            step={1}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            Maximum sequence length for pattern matching (3-15)
          </p>
        </div>

        <div className="space-y-2">
          <Label>Win Threshold: {(config.winThreshold * 100).toFixed(0)}%</Label>
          <Slider
            value={[config.winThreshold]}
            onValueChange={([value]) => setConfig(prev => ({ ...prev, winThreshold: value }))}
            min={0.5}
            max={0.9}
            step={0.01}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">
            Threshold for WIN/LOSS decisions (50%-90%)
          </p>
        </div>

        <Button 
          onClick={handleRunBacktest} 
          disabled={isRunning || !selectedFile}
          className="w-full"
        >
          <Play className="h-4 w-4 mr-2" />
          {isRunning ? 'Running Backtest...' : 'Start Progressive Backtest'}
        </Button>

        {isRunning && progress && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress: {progress.completed}/{progress.total}</span>
              <span>Accuracy: {progress.accuracy.toFixed(1)}%</span>
            </div>
            <Progress value={(progress.completed / progress.total) * 100} />
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderResults = () => {
    if (!currentResult) return null;

    const learningCurveData = currentResult.learningCurve.map(point => ({
      position: point.position,
      accuracy: point.runningAccuracy,
      predictions: point.predictionsCount
    }));

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-sm font-medium">Accuracy</p>
                  <p className="text-2xl font-bold">{currentResult.accuracy.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <div>
                  <p className="text-sm font-medium">Predictions</p>
                  <p className="text-2xl font-bold">{currentResult.predictedPositions}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-orange-500" />
                <div>
                  <p className="text-sm font-medium">Skip Rate</p>
                  <p className="text-2xl font-bold">{currentResult.skipRate.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-blue-500" />
                <div>
                  <p className="text-sm font-medium">Correct</p>
                  <p className="text-2xl font-bold">{currentResult.correctPredictions}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Skip Analytics */}
        {currentResult.skipAnalytics && currentResult.skipAnalytics.totalSkips > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Skip Analysis</CardTitle>
              <CardDescription>
                Detailed analysis of skipped predictions and their actual outcomes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium text-muted-foreground">Skip Efficiency</p>
                  <p className="text-2xl font-bold text-green-600">
                    {currentResult.skipAnalytics.skipEfficiency.toFixed(1)}%
                  </p>
                  <p className="text-xs text-muted-foreground">Good skips / Total skips</p>
                </div>
                
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium text-muted-foreground">Potential Accuracy</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {currentResult.skipAnalytics.potentialAccuracy.toFixed(1)}%
                  </p>
                  <p className="text-xs text-muted-foreground">If didn't skip missed wins</p>
                </div>
                
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium text-muted-foreground">Missed Opportunities</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {currentResult.skipAnalytics.missedOpportunities}
                  </p>
                  <p className="text-xs text-muted-foreground">Should have predicted</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="bg-green-50 p-3 rounded text-center">
                  <p className="font-medium text-green-700">Good Skip (Avoided Loss)</p>
                  <p className="text-lg font-bold text-green-600">
                    {currentResult.skipAnalytics.skipBreakdown.goodSkipLosses}
                  </p>
                </div>
                
                <div className="bg-green-50 p-3 rounded text-center">
                  <p className="font-medium text-green-700">Good Skip (Avoided Win)</p>
                  <p className="text-lg font-bold text-green-600">
                    {currentResult.skipAnalytics.skipBreakdown.goodSkipWins}
                  </p>
                </div>
                
                <div className="bg-orange-50 p-3 rounded text-center">
                  <p className="font-medium text-orange-700">Missed Win</p>
                  <p className="text-lg font-bold text-orange-600">
                    {currentResult.skipAnalytics.skipBreakdown.missedWins}
                  </p>
                </div>
                
                <div className="bg-orange-50 p-3 rounded text-center">
                  <p className="font-medium text-orange-700">Missed Loss</p>
                  <p className="text-lg font-bold text-orange-600">
                    {currentResult.skipAnalytics.skipBreakdown.missedLosses}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Learning Curve Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Learning Curve</CardTitle>
            <CardDescription>
              Accuracy evolution as more data becomes available
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={learningCurveData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="position" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      name === 'accuracy' ? `${value.toFixed(1)}%` : value,
                      name === 'accuracy' ? 'Accuracy' : 'Predictions'
                    ]}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="accuracy" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Export Button */}
        <div className="flex justify-end">
          <Button onClick={handleDownloadResults} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Results
          </Button>
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="space-y-4">
      {isLoadingHistory ? (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Loading simulation history...</p>
        </div>
      ) : simulations.length === 0 ? (
        <div className="text-center py-8">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-lg font-medium mb-2">No simulations yet</p>
          <p className="text-sm text-muted-foreground">
            Run your first progressive backtest to see results here
          </p>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Simulation History</CardTitle>
            <CardDescription>
              Previous progressive backtest simulations for this file
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Start Pos</TableHead>
                  <TableHead>Max Seq</TableHead>
                  <TableHead>Threshold</TableHead>
                  <TableHead>Accuracy</TableHead>
                  <TableHead>Predictions</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {simulations.map((sim) => (
                  <TableRow key={sim.id}>
                    <TableCell>
                      {new Date(sim.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{sim.analysis_mode}</Badge>
                    </TableCell>
                    <TableCell>{sim.start_position}</TableCell>
                    <TableCell>{sim.max_sequence_length}</TableCell>
                    <TableCell>{(sim.win_threshold * 100).toFixed(0)}%</TableCell>
                    <TableCell>
                      <Badge variant={sim.accuracy >= 60 ? 'default' : 'secondary'}>
                        {sim.accuracy.toFixed(1)}%
                      </Badge>
                    </TableCell>
                    <TableCell>{sim.predicted_positions}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedSimulation(sim)}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteSimulation(sim.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );

  if (!selectedFile) {
    return (
      <div className="text-center py-8">
        <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-lg font-medium mb-2">No File Selected</p>
        <p className="text-sm text-muted-foreground">
          Please select a trading file to run progressive backtest
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Progressive Backtest</h3>
          <p className="text-sm text-muted-foreground">
            Simulate real-time trading with progressive sequence growth
          </p>
        </div>
        <Badge variant="outline">File: {selectedFile.filename}</Badge>
      </div>

      <Tabs defaultValue="configuration" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="configuration">
          {renderConfigurationForm()}
        </TabsContent>

        <TabsContent value="results">
          {currentResult ? renderResults() : (
            <div className="text-center py-8">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-lg font-medium mb-2">No Results Yet</p>
              <p className="text-sm text-muted-foreground">
                Configure and run a progressive backtest to see results
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          {renderHistory()}
        </TabsContent>
      </Tabs>

      {/* Simulation Detail Dialog */}
      <SimulationDetailDialog
        simulation={selectedSimulation}
        open={!!selectedSimulation}
        onClose={() => setSelectedSimulation(null)}
      />
    </div>
  );
};